import { Component } from 'vue-property-decorator'
import { mixins } from 'vue-class-component'

import { caseType, caseTypeClass, caseTypeToVerb, caseStatusKeys, caseStatusValues, casePriority } from '@/modules/Case/helpers/case'
import { ApolloResponse } from '@/interfaces/apollo'
import { Breadcrumb } from '@/interfaces/breadcrumbs'
import { Case, CaseType } from '@/modules/Case/interfaces/case'
import { ReducedUser } from '@/modules/User/interfaces/user'
import { showErrors, showNotification } from '@/utils/snack'
import { dateTimeToDisplay, dateToDisplay, dateToNow } from '@/utils/dateTime'
import { translateCaseTypeKey } from '@/utils/translate'
import AppBreadcrumbs from '@/components/AppBreadcrumbs.vue'
import AppSearch from '@/components/AppSearch.vue'
import AppRefresh from '@/components/AppRefresh.vue'
import AppTabs from '@/components/AppTabs.vue'

import CaseDialog from '@/modules/Case/scripts/case-dialog'
import CaseDetailDetail from '@/modules/Case/views/detail.vue'
import { getCaseById, editCaseById } from '@/modules/Case/gql/case.gql'
import CasePrioritySwitcher from '@/modules/Case/components/CasePrioritySwitcher.vue'
import { policyStatus, policyStatusClass } from '@/modules/Policy/helpers/policy'
import AssignToDialog from '@/components/AssignToDialog.vue'
import AppListSelector from '@/components/AppListSelector.vue'
import { SnackType } from '@/interfaces/snack'

@Component({
  components: {
    AppBreadcrumbs,
    AppSearch,
    AppRefresh,
    AppTabs,
    CaseDetailDetail,
    AppListSelector,
    CasePrioritySwitcher,
    AssignToDialog
  }
})
export default class CaseDetail extends mixins(CaseDialog) {
  breadcrumbs: Breadcrumb[] = [
    {
      text: 'Insurance Cases'
    },
    {
      text: 'Case',
      to: { name: 'CaseDashboard' }
    }
  ]

  initState = true

  caseInfo: Case | null = null

  tabActive = 0
  tabItems = [
    {
      title: 'Details',
      route: 'CaseDetailDetail'
    }
  ]

  staffList: ReducedUser[] = []

  translateCaseTypeKey = (key: string) => translateCaseTypeKey(key)
  dateTimeToDisplay = dateTimeToDisplay
  dateToDisplay = dateToDisplay
  dateToNow = dateToNow

  policyStatus = policyStatus
  policyStatusClass = policyStatusClass
  caseTypeClass = caseTypeClass
  casePriority = casePriority
  caseType = caseType

  activeLock = false
  activeUnlock = false
  activeAssign = false
  activeDelete = false

  caseTypeToVerb = caseTypeToVerb

  caseStatusValues = caseStatusValues
  caseStatusKeys = caseStatusKeys

  get tabTitles () {
    return this.tabItems.map(item => item.title)
  }

  get staffNameList () {
    return this.staffList.map(staff => staff.username)
  }

  get stepIndex () {
    const { caseInfo } = this
    if (!caseInfo) {
      return 0
    }
    return this.caseStatusKeys.findIndex((key: string) => key === caseInfo.status)
  }

  get openedDays () {
    if (!this.caseInfo) {
      return ''
    }

    const { openDate, closed } = this.caseInfo

    if (!openDate) {
      return '-'
    }

    const openDateM = this.$moment.unix(openDate).tz('America/New_York')
    const nowM = this.$moment().tz('America/New_York')

    return closed
      ? '-'
      : nowM.diff(openDateM, 'days')
  }

  get infoColor () {
    if (this.openedDays > 7) {
      return 'color-red'
    }
    if (this.openedDays >= 5) {
      return 'color-yellow'
    }
  }

  mounted () {
    if (this.caseInfo) {
      this.breadcrumbs.push({
        text: `${this.caseInfo.inquiryName} (Case ID: ${this.caseInfo.id})`
      })
    }
    this.getCase()
  }

  setTabItems () {
    if (!this.caseInfo) {
      return
    }
    if (this.caseInfo.type === CaseType.ENDORSEMENT) {
      this.tabItems = [
        {
          title: 'Details',
          route: 'CaseDetailDetail'
        },
        {
          title: 'Endorsement',
          route: 'CaseDetailEndorsement'
        },
        {
          title: 'Related',
          route: 'CaseDetailRelated'
        },
        {
          title: 'Source',
          route: 'CaseDetailSource'
        },
        {
          title: 'Attachments',
          route: 'CaseDetailAttachments'
        },
        {
          title: 'Additional',
          route: 'CaseDetailAdditional'
        },
        {
          title: 'Notes',
          route: 'CaseDetailNote'
        }
      ]
    } else {
      this.tabItems = [
        {
          title: 'Details',
          route: 'CaseDetailDetail'
        },
        {
          title: 'Quote',
          route: 'CaseDetailQuote'
        },
        {
          title: 'Policy',
          route: 'CaseDetailPolicy'
        },
        {
          title: 'Related',
          route: 'CaseDetailRelated'
        },
        {
          title: 'Source',
          route: 'CaseDetailSource'
        },
        {
          title: 'Attachments',
          route: 'CaseDetailAttachments'
        },
        {
          title: 'Additional',
          route: 'CaseDetailAdditional'
        },
        {
          title: 'Notes',
          route: 'CaseDetailNote'
        }
      ]
    }

    const { name } = this.$route
    const index = this.$R.findIndex((item: any) => item.route === name)(this.tabItems)
    this.tabActive = index
  }

  clickTabHandler (index: number) {
    const item = this.tabItems[index]
    if (item.route && item.route !== this.$route.name) {
      this.$router.push({
        name: item.route
      })
    }
  }

  async deleteCaseConfirm () {
    await this.deleteCasePost()
    this.$router.replace({
      name: 'CaseDashboard'
    })
  }

  async assignCaseConfirm (userIds: number[]) {
    await this.assignCasePost(userIds)
    this.getCase()
  }

  async assignCaseCancel () {
    this.assignDialog.show = false
  }

  async closeCaseConfirm () {
    if (this.closeCaseDialog.isClose) {
      await this.closeCasePost()
    } else {
      await this.openCasePost()
    }
    this.getCase()
  }

  async switchCaseStatusConfirm () {
    await this.switchCaseStatusPost()
    this.getCase()
  }

  async getCase () {
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getCaseById,
        variables: {
          caseId: Number(this.$route.params.id)
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      this.caseInfo = result.data.getCaseById
      if (this.breadcrumbs.length === 2) {
        this.breadcrumbs.push({
          text: `${(this.caseInfo as Case).inquiryName} (Case ID: ${(this.caseInfo as Case).id})`
        })
      }
      this.setTabItems()
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  routeToAccountDetail () {
    if (this.caseInfo && this.caseInfo.account) {
      this.$router.push({
        name: 'AccountDetailDetail',
        params: {
          id: this.caseInfo.account.id + ''
        }
      })
    }
  }

  routeToContactDetail () {
    if (this.caseInfo && this.caseInfo.primaryContact) {
      this.$router.push({
        name: 'ContactDetailDetail',
        params: {
          id: this.caseInfo.primaryContact.id + ''
        }
      })
    }
  }

  routeToProducerDetail () {
    if (this.caseInfo && this.caseInfo.source) {
      this.$router.push({
        name: 'ProducerDetailDetail',
        params: {
          id: this.caseInfo.source.id + ''
        }
      })
    }
  }

  async sendImportantChangeRequest (wfCase: Case, important: boolean) {
    try {
      const res = await this.$apollo.mutate({
        mutation: editCaseById,
        variables: {
          caseId: +wfCase.id,
          caseInput: {
            inquiryName: wfCase.inquiryName,
            important
          }
        }
      })
      this.getCase()
      showNotification({
        msg: 'Update successfully',
        type: SnackType.Success
      })
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  // 编辑紧急性

  async sendUrgentChangeRequest (wfCase: Case, urgent: boolean) {
    try {
      const res = await this.$apollo.mutate({
        mutation: editCaseById,
        variables: {
          caseId: +wfCase.id,
          caseInput: {
            inquiryName: wfCase.inquiryName,
            urgent
          }
        }
      })
      this.getCase()
      showNotification({
        msg: 'Update successfully',
        type: SnackType.Success
      })
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }
}
